import type { ItemSortField } from '../types/item';

const STORAGE_KEY = 'brix.productsTable';

export type SortDir = 'asc' | 'desc';

export const ITEM_SORT_FIELDS: readonly ItemSortField[] = [
  'name',
  'sku',
  'item_type',
  'unit_price',
  'standard_cost',
  'dealer_selling_price',
  'cash_price',
  'qty_on_hand',
  'active',
  'created_at',
] as const;

const ITEM_SORT_FIELD_SET = new Set<string>(ITEM_SORT_FIELDS);

export type ProductsTablePreferences = {
  sort: ItemSortField | `-${ItemSortField}`;
  perPage: number;
};

const DEFAULT_PREFERENCES: ProductsTablePreferences = {
  sort: 'name',
  perPage: 100,
};

export const encodeItemSort = (field: ItemSortField, direction: SortDir): ProductsTablePreferences['sort'] =>
  direction === 'desc' ? `-${field}` : field;

export const parseItemSort = (
  value: string
): { field: ItemSortField; direction: SortDir } | null => {
  const direction: SortDir = value.startsWith('-') ? 'desc' : 'asc';
  const field = (value.startsWith('-') ? value.slice(1) : value) as ItemSortField;

  if (!ITEM_SORT_FIELD_SET.has(field)) {
    return null;
  }

  return { field, direction };
};

export const loadProductsTablePreferences = (): ProductsTablePreferences => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(raw) as Partial<ProductsTablePreferences>;
    const sort =
      typeof parsed.sort === 'string' && parseItemSort(parsed.sort)
        ? (parsed.sort as ProductsTablePreferences['sort'])
        : DEFAULT_PREFERENCES.sort;
    const perPage =
      typeof parsed.perPage === 'number' && parsed.perPage >= 1 && parsed.perPage <= 100
        ? parsed.perPage
        : DEFAULT_PREFERENCES.perPage;

    return { sort, perPage };
  } catch {
    return DEFAULT_PREFERENCES;
  }
};

export const saveProductsTablePreferences = (prefs: ProductsTablePreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore quota / private browsing errors.
  }
};
